import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { validateCompanyCode } from '@app/lib/company-code/validateCompanyCode';
import { purchaseTransportInsurance } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { TransportPremiumsDiscount } from '@app/domain/model/eshop/transport/TransportPremiumsDiscount';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';

export interface PersonalCodeData {
    isValid: boolean;
    age?: number;
}

export interface PriceState {
    selectedMtplPremium: Nullable<TransportPremiumsResponse>;
    selectedCascoPremium: Nullable<TransportPremiumsResponse>;
    selectedRiders: ProductDataRider[];
}

export function parsePersonalCode(personalCode: string): PersonalCodeData {
    return parseLithuanianPersonalCode(personalCode);
}

export function validateCompanyCodeData(personalCode: string): PersonalCodeData {
    return validateCompanyCode(personalCode);
}

export function createEnhancedFormAction(
    requestResult: TransportInsuranceRequestResult,
    discountCodeData: DiscountCodeData,
    personalCodeData: PersonalCodeData,
    priceState: PriceState,
    isReinitializedSession: boolean,
) {
    return async (prevState: FormState<PurchaseTransportInsuranceResult>, formData: FormData) => {
        if (discountCodeData.validEcaPapCode) {
            formData.append('discountCodeEcaPap', discountCodeData.validEcaPapCode);
        }

        if (discountCodeData.validCascoCode) {
            formData.append('discountCodeCasco', discountCodeData.validCascoCode);
        }

        if (priceState.selectedMtplPremium) {
            formData.append('mtplPolicyGuid', priceState.selectedMtplPremium.code);
            formData.append('mtplPolicyPackageCode', priceState.selectedMtplPremium.riskPackageCode);
        }

        if (priceState.selectedCascoPremium) {
            formData.append('cascoPolicyGuid', priceState.selectedCascoPremium.code);
            formData.append('cascoPolicyPackageCode', priceState.selectedCascoPremium.riskPackageCode);
        }

        if (priceState.selectedRiders.length > 0) {
            priceState.selectedRiders.forEach((rider: ProductDataRider) => {
                formData.append(
                    'riskTypeCodes',
                    JSON.stringify({
                        code: rider.typeCode,
                        id: rider.riderGuid,
                    }),
                );
            });
        }

        if (priceState.selectedMtplPremium?.discounts && priceState.selectedMtplPremium.discounts.length > 0) {
            const completeDiscounts = priceState.selectedMtplPremium.discounts.map(
                (discount: TransportPremiumsDiscount) => ({
                    code: discount.code,
                    type: discount.type,
                    discount: discount.discount,
                }),
            );

            completeDiscounts.forEach((discount: TransportPremiumsDiscount) => {
                formData.append('discountDataMtpl', JSON.stringify(discount));
            });
        }

        if (priceState.selectedCascoPremium?.discounts && priceState.selectedCascoPremium.discounts.length > 0) {
            const completeDiscounts = priceState.selectedCascoPremium.discounts.map(
                (discount: TransportPremiumsDiscount) => ({
                    code: discount.code,
                    type: discount.type,
                    discount: discount.discount,
                }),
            );

            completeDiscounts.forEach((discount: TransportPremiumsDiscount) => {
                formData.append('discountDataCasco', JSON.stringify(discount));
            });
        }

        return purchaseTransportInsurance(
            requestResult,
            requestResult.vehicleInformation,
            requestResult.policyValidity ?? null,
            isReinitializedSession,
            prevState,
            formData,
        );
    };
}
